// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — components/PessoaLookupCombobox.tsx
//
//  Pessoa (wrd_clientes) por papel — cliente, fornecedor, vendedor, funcionário,
//  motorista — com busca NO SERVIDOR (nome, apelido, CPF/CNPJ, e-mail, código).
//  Exibe "Código — Nome". API: GET /api/clientes/lookup?busca=&papel=.
//  Wrappers prontos: VendedorSearchCombobox, ColaboradorSearchCombobox.
// ════════════════════════════════════════════════════════════════════════════

import { EntityCombobox } from "@/components/EntityCombobox";
import { apiClient } from "@/lib/apiClient";
import type { LookupItem } from "@/lib/consultas";

export type PapelPessoa = "cliente" | "fornecedor" | "vendedor" | "funcionario" | "motorista";

const BASE = "/api/clientes";

async function obterPessoa(id: number): Promise<LookupItem> {
  const { data } = await apiClient.get<{ id: number; nomeRazao: string; cpfCnpj?: string | null }>(`${BASE}/${id}`);
  return { id: data.id, codigo: String(data.id), descricao: data.nomeRazao, extra: data.cpfCnpj };
}

export interface PessoaLookupComboboxProps {
  value?: number | null;
  onChange?: (id: number | null) => void;
  onSelect?: (item: LookupItem | null) => void;
  /** Sem papel = qualquer pessoa. */
  papel?: PapelPessoa;
  /** Nome já conhecido (DTO em edição): exibe sem GET. */
  nomeInicial?: string | null;
  placeholder?: string;
  permitirLimpar?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export function PessoaLookupCombobox({
  papel, nomeInicial, placeholder = "Selecione...", ...props
}: PessoaLookupComboboxProps) {
  const itemInicial = props.value && nomeInicial
    ? { id: props.value, codigo: String(props.value), descricao: nomeInicial }
    : null;
  return (
    <EntityCombobox<LookupItem>
      {...props}
      itemInicial={itemInicial}
      queryKey={`pessoa-lookup-${papel ?? "todas"}`}
      buscar={async (termo) => {
        const { data } = await apiClient.get<LookupItem[]>(`${BASE}/lookup`, {
          params: { busca: termo || undefined, papel, limite: 30 },
        });
        return data;
      }}
      obterPorId={obterPessoa}
      getId={(p) => p.id}
      getCodigo={(p) => p.codigo}
      getDescricao={(p) => p.descricao}
      getExtra={(p) => p.extra}
      placeholder={placeholder}
      placeholderBusca="Buscar por código, nome ou CPF/CNPJ..."
    />
  );
}
